import { type YDatabase } from '@databases/collab/y-database';

import { type CollabManagerLike } from './collab-manager-like.ts';

/**
 * Thrown when {@link loadDatabase} does not receive its initial Yjs document
 * within the configured timeout.
 */
export class LoadTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Database failed to load within ${timeoutMs}ms`);
    this.name = 'LoadTimeoutError';
  }
}

/**
 * Wraps `CollabManager.onLoaded` in a promise that races against both a
 * timeout and connection errors. Rejects immediately if `onError` fires
 * before the document loads, rather than waiting for the full timeout.
 */
export function awaitLoaded(
  manager: CollabManagerLike,
  timeoutMs: number
): Promise<YDatabase> {
  return new Promise<YDatabase>((resolve, reject) => {
    let settled = false;

    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new LoadTimeoutError(timeoutMs));
      }
    }, timeoutMs);

    manager.onError((error) => {
      if (!settled) {
        settled = true;
        clearTimeout(timeout);
        reject(error);
      }
    });

    manager.onLoaded((ydb) => {
      if (!settled) {
        settled = true;
        clearTimeout(timeout);
        resolve(ydb);
      }
    });
  });
}
